// store/cartStore.js - UPDATED VERSION WITH LOGOUT HANDLING
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import useUserStore from './userStore';

// Safe methods for localStorage in Next.js environment
const getLocalStorage = () => {
  if (typeof window !== 'undefined') {
    return window.localStorage;
  }
  return null;
};

const getItemFromStorage = (key, defaultValue = null) => {
  const storage = getLocalStorage();
  if (storage) {
    try {
      return storage.getItem(key) || defaultValue;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return defaultValue;
    }
  }
  return defaultValue;
};

const setItemInStorage = (key, value) => {
  const storage = getLocalStorage();
  if (storage) {
    try {
      storage.setItem(key, value);
    } catch (error) {
      console.error('Error writing to localStorage:', error);
    }
  }
};

// Calculate total price from cart items
const calculateTotalPrice = (cartItems) => {
  return cartItems.reduce((total, item) => {
    const price =
      item.product && typeof item.product.price === 'number'
        ? item.product.price
        : 0;
    return total + price;
  }, 0);
};

// Generate a new cart token (extracted as a utility function)
const generateCartToken = () => {
  return `cart_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
};

// Utility to implement debouncing
const debounce = (func, wait) => {
  let timeout;
  return function (...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Track last cart fetch time to prevent excessive requests
let lastCartFetchTime = 0;
const MIN_CART_FETCH_INTERVAL = 30 * 1000; // 30 seconds minimum between fetches

// Create a unique tab identifier
const TAB_ID = Date.now().toString() + Math.random().toString(36).substring(2, 9);

// NEW: BroadcastChannel for tab synchronization
let cartChannel = null;

// Initialize BroadcastChannel on client side
if (typeof window !== 'undefined') {
  try {
    cartChannel = new BroadcastChannel('cart-sync');
  } catch (error) {
    console.error('Failed to initialize BroadcastChannel:', error);
    cartChannel = null;
  }
}

// Flag to track if we're handling a broadcast message
let handlingBroadcast = false;

// NEW: Function to broadcast cart changes to other tabs
const broadcastCartChange = (type, payload = {}) => {
  if (cartChannel && !handlingBroadcast) {
    try {
      cartChannel.postMessage({
        type,
        payload: {
          ...payload,
          timestamp: Date.now(),
          tabId: TAB_ID, // Include tab identifier
        },
      });
    } catch (error) {
      console.error('Failed to broadcast cart change:', error);
    }
  }
};

// Direct state update function that doesn't trigger broadcast
const silentlyUpdateState = (updates) => {
  handlingBroadcast = true;
  try {
    useCartStore.setState(updates);
  } finally {
    // Ensure the flag is reset even if there's an error
    setTimeout(() => {
      handlingBroadcast = false;
    }, 0);
  }
};

// Function to fetch cart from server directly without triggering broadcast
const fetchCartFromServer = async (cartToken, forceRefresh = false) => {
  if (typeof window === 'undefined') return null;

  try {
    const BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL;

    const response = await useUserStore.getState().fetchWithTokenRefresh(
      `${BACKEND_URL}/cart/get_or_create_cart`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
        },
        body: JSON.stringify({
          cart_token: cartToken,
        }),
        requireAuth: false, // Cart works for guests too
      }
    );

    if (response.ok) {
      const data = await response.json();

      const items = data.items.map((item) => {
        const asset = item.asset;
        let price = 0;
        if (asset.media_type === 'icon') price = 2;
        else if (asset.media_type === 'iconset') price = 20;

        return {
          id: asset.id,
          assetId: asset.id,
          product: { price },
          mediaType: asset.media_type,
          added_at: item.added_at,
          slug: asset.slug,
          collection: asset.collection,
        };
      });

      // Update last fetch time
      lastCartFetchTime = Date.now();

      return {
        items,
        cartToken: data.cart_token || cartToken,
        success: true,
      };
    }

    return { success: false };
  } catch (error) {
    console.error('Error fetching cart from server:', error);
    return { success: false };
  }
};

const useCartStore = create(
  persist(
    (set, get) => {
      // Initialize cartToken from localStorage first
      let initialCartToken = null;
      if (typeof window !== 'undefined') {
        initialCartToken = getItemFromStorage('cart_token');

        // Create a cart token if it doesn't exist
        if (!initialCartToken) {
          initialCartToken = generateCartToken();
          setItemInStorage('cart_token', initialCartToken);
        }
      }

      return {
        cartToken: initialCartToken,
        isOpen: false,
        cartItems: [],
        totalPrice: 0,
        lastOperation: null,

        // Separate operation flags
        fetchInProgress: false,
        addInProgress: false,
        removeInProgress: false,
        clearInProgress: false,

        authListenerInitialized: false,
        lastAuthState: null,

        // Track if cart has been fetched this session
        cartFetched: false,

        openCart: () => set({ isOpen: true }),
        closeCart: () => set({ isOpen: false }),
        toggleCart: () => set((state) => ({ isOpen: !state.isOpen })),

        setCartItems: (items) => {
          set({
            cartItems: items,
            totalPrice: calculateTotalPrice(items),
          });
          if (!handlingBroadcast) {
            broadcastCartChange('CART_UPDATED');
          }
        },

        // Setter for cart token
        setCartToken: (token) => {
          if (!token) {
            console.error('Attempted to set empty cart token');
            return;
          }
          setItemInStorage('cart_token', token);
          set({ cartToken: token });
          if (!handlingBroadcast) {
            broadcastCartChange('TOKEN_CHANGED', { token });
          }
        },

        // Get access token with proper handling for Next.js
        getAccessToken: async () => {
          if (typeof window === 'undefined') return null;

          const { accessToken, loading } = useUserStore.getState();
          if (!loading) {
            return accessToken;
          }

          return new Promise((resolve) => {
            const unsubscribe = useUserStore.subscribe((state) => {
              if (!state.loading) {
                resolve(state.accessToken);
                unsubscribe();
              }
            });

            setTimeout(() => {
              resolve(null);
              unsubscribe();
            }, 2000);
          });
        },

        ensureCartToken: () => {
          const { cartToken } = get();
          if (cartToken) {
            return cartToken;
          }
          const newToken = generateCartToken();
          set({ cartToken: newToken });
          setItemInStorage('cart_token', newToken);
          if (!handlingBroadcast) {
            broadcastCartChange('TOKEN_CHANGED', { token: newToken });
          }
          return newToken;
        },

        // Add auth state change listener to sync cart on login/logout
        initAuthListener: () => {
          if (get().authListenerInitialized || typeof window === 'undefined') {
            return;
          }

          // Subscribe to userStore changes - with debouncing
          const debouncedHandleAuthChange = debounce((isLoggedIn, loading) => {
            if (loading) return;
            const currentCart = get();
            if (currentCart.lastAuthState !== isLoggedIn) {
              set({ lastAuthState: isLoggedIn });
              const now = Date.now();
              if (now - lastCartFetchTime > MIN_CART_FETCH_INTERVAL) {
                set({ fetchInProgress: true });
                set({
                  addInProgress: false,
                  removeInProgress: false,
                  clearInProgress: false,
                });
                lastCartFetchTime = now;
                setTimeout(() => {
                  get().fetchCartContents(true);
                }, 100);
              }
            }
          }, 300);

          const unsubscribe = useUserStore.subscribe((state) => {
            const { isLoggedIn, loading } = state;
            debouncedHandleAuthChange(isLoggedIn, loading);
          });

          set({
            authListenerInitialized: true,
            lastAuthState: useUserStore.getState().isLoggedIn,
          });
        },

        // Updated fetchCartContents method with BroadcastChannel sync
        fetchCartContents: async (forceRefresh = false) => {
          if (typeof window === 'undefined') return true;
          if (get().cartFetched && !forceRefresh) {
            return true;
          }
          if (!forceRefresh && get().fetchInProgress) {
            return true;
          }
          const now = Date.now();
          if (!forceRefresh && now - lastCartFetchTime < MIN_CART_FETCH_INTERVAL) {
            return true;
          }
          lastCartFetchTime = now;
          set({ fetchInProgress: true, lastOperation: 'fetchCartContents' });

          try {
            const cartToken = get().ensureCartToken();
            const result = await fetchCartFromServer(cartToken, forceRefresh);

            if (result.success) {
              const { items, cartToken: newCartToken } = result;
              const localItems = get().cartItems;
              const mergedItems = [...items];

              // Add local items that aren't on the server
              localItems.forEach((localItem) => {
                if (!mergedItems.some((item) => item.assetId === localItem.assetId)) {
                  mergedItems.push(localItem);
                }
              });

              set({
                cartItems: mergedItems,
                totalPrice: calculateTotalPrice(mergedItems),
                fetchInProgress: false,
                cartFetched: true,
              });

              if (newCartToken && newCartToken !== cartToken) {
                set({ cartToken: newCartToken });
                setItemInStorage('cart_token', newCartToken);
                if (!handlingBroadcast) {
                  broadcastCartChange('TOKEN_CHANGED', { token: newCartToken });
                }
              }
              if (!handlingBroadcast) {
                broadcastCartChange('CART_UPDATED');
              }
              return true;
            } else {
              set({
                fetchInProgress: false,
                cartFetched: true,
              });
              return true;
            }
          } catch (error) {
            console.error('Failed to fetch cart contents:', error);
            set({
              fetchInProgress: false,
              cartFetched: true,
            });
            return true;
          }
        },

        // Updated addToCart method with BroadcastChannel sync
        addToCart: async (asset) => {
          if (typeof window === 'undefined') return false;
          if (get().addInProgress) {
            return true;
          }

          set({ addInProgress: true, lastOperation: 'addToCart' });

          try {
            let cartToken = get().ensureCartToken();
            const BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL;
            const { cartItems } = get();

            const existingItem = cartItems.find(
              (item) => item.assetId === asset.assetId
            );
            if (existingItem) {
              set({ addInProgress: false });
              return true;
            }

            const updatedCart = [...cartItems, asset];
            set({
              cartItems: updatedCart,
              totalPrice: calculateTotalPrice(updatedCart),
            });

            if (!handlingBroadcast) {
              broadcastCartChange('ITEM_ADDED', { item: asset });
            }

            try {
              let success = false;
              let retries = 0;
              const maxRetries = 2;

              while (!success && retries <= maxRetries) {
                try {
                  const currentToken = get().cartToken;
                  const response = await useUserStore
                    .getState()
                    .fetchWithTokenRefresh(`${BACKEND_URL}/cart/add`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Cache-Control': 'no-cache',
                      },
                      body: JSON.stringify({
                        cart_token: currentToken,
                        asset_id: asset.assetId,
                      }),
                      requireAuth: false,
                    });

                  if (response.ok) {
                    const data = await response.json();
                    if (data.cart_token && data.cart_token !== currentToken) {
                      set({ cartToken: data.cart_token });
                      setItemInStorage('cart_token', data.cart_token);
                      if (!handlingBroadcast) {
                        broadcastCartChange('TOKEN_CHANGED', {
                          token: data.cart_token,
                        });
                      }
                    }
                    success = true;
                    break;
                  } else {
                    let errorData;
                    try {
                      errorData = await response.json();
                    } catch (e) {
                      errorData = { detail: 'Unknown error' };
                    }

                    if (
                      errorData.detail === 'Cart not found' ||
                      response.status === 404
                    ) {
                      const newToken = generateCartToken();
                      set({ cartToken: newToken });
                      setItemInStorage('cart_token', newToken);
                      if (!handlingBroadcast) {
                        broadcastCartChange('TOKEN_CHANGED', { token: newToken });
                      }
                      try {
                        await useUserStore
                          .getState()
                          .fetchWithTokenRefresh(
                            `${BACKEND_URL}/cart/get_or_create_cart`,
                            {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Cache-Control': 'no-cache',
                              },
                              body: JSON.stringify({
                                cart_token: newToken,
                              }),
                              requireAuth: false,
                            }
                          );
                      } catch (e) {
                        console.error('Error creating new cart:', e);
                      }
                    }
                    retries++;
                    await new Promise((resolve) => setTimeout(resolve, 500));
                    continue;
                  }
                } catch (error) {
                  console.error('Error in addToCart background sync:', error);
                  retries++;
                  await new Promise((resolve) =>
                    setTimeout(resolve, 800 * retries)
                  );
                }
              }

              // Keep item in local cart even if sync fails
            } finally {
              set({ addInProgress: false });
            }

            return true;
          } catch (error) {
            console.error('Unexpected error in addToCart:', error);
            set({ addInProgress: false });
            return true;
          }
        },

        // Updated removeFromCart method with BroadcastChannel sync
        removeFromCart: async (assetId) => {
          if (typeof window === 'undefined') return false;
          if (get().removeInProgress) {
            return false;
          }

          set({ removeInProgress: true, lastOperation: 'removeFromCart' });

          try {
            const cartToken = get().ensureCartToken();
            const { cartItems } = get();
            const BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL;

            const updatedCart = cartItems.filter(
              (item) => item.assetId !== assetId
            );
            set({
              cartItems: updatedCart,
              totalPrice: calculateTotalPrice(updatedCart),
            });

            if (!handlingBroadcast) {
              broadcastCartChange('ITEM_REMOVED', { assetId });
            }

            const response = await useUserStore
              .getState()
              .fetchWithTokenRefresh(`${BACKEND_URL}/cart/remove`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Cache-Control': 'no-cache',
                },
                body: JSON.stringify({
                  cart_token: cartToken,
                  asset_id: assetId,
                }),
                requireAuth: false,
              });

            if (!response.ok) {
              console.error('Error removing from cart, but keeping UI updated');
            }

            set({ removeInProgress: false });
            return true;
          } catch (error) {
            console.error('Network error removing from cart:', error);
            set({ removeInProgress: false });
            return true;
          }
        },

        // UPDATED: Improved clearCart method
        clearCart: async () => {
          if (typeof window === 'undefined') return false;
          if (get().clearInProgress) {
            return false;
          }

          set({ clearInProgress: true, lastOperation: 'clearCart' });

          try {
            const cartToken = get().ensureCartToken();
            const BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL;

            set({
              cartItems: [],
              totalPrice: 0,
            });

            if (!handlingBroadcast) {
              broadcastCartChange('CART_CLEARED', { cartToken });
            }

            const response = await useUserStore
              .getState()
              .fetchWithTokenRefresh(`${BACKEND_URL}/cart/clear`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Cache-Control': 'no-cache',
                },
                body: JSON.stringify({
                  cart_token: cartToken,
                }),
                requireAuth: false,
              });

            let success = false;
            if (response.ok) {
              success = true;
            } else {
              console.error(
                'Error clearing cart on server:',
                await response.text()
              );
            }

            set({ clearInProgress: false });
            return success;
          } catch (error) {
            console.error('Network error clearing cart:', error);
            set({ clearInProgress: false });
            return true;
          }
        },
        
        // NEW: Function to handle logout - creates a new cart and transfers items
        handleLogout: async () => {
          if (typeof window === 'undefined') return false;
          
          try {
            // Save current cart items
            const currentItems = [...get().cartItems];
            
            // Generate a new cart token
            const newCartToken = generateCartToken();
            
            // Update the cart token in storage and state
            setItemInStorage('cart_token', newCartToken);
            set({ 
              cartToken: newCartToken,
              // Don't clear cart items immediately - we'll preserve them
            });
            
            if (!handlingBroadcast) {
              broadcastCartChange('TOKEN_CHANGED', { token: newCartToken });
            }
            
            // Create the new cart on the server
            const BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL;
            try {
              await useUserStore
                .getState()
                .fetchWithTokenRefresh(
                  `${BACKEND_URL}/cart/get_or_create_cart`,
                  {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Cache-Control': 'no-cache',
                    },
                    body: JSON.stringify({
                      cart_token: newCartToken,
                    }),
                    requireAuth: false,
                  }
                );
                
              // Transfer items to the new cart
              for (const item of currentItems) {
                await get().addToCart(item);
              }
              
              return true;
            } catch (e) {
              console.error('Error creating new cart during logout:', e);
              return false;
            }
          } catch (error) {
            console.error('Error in handleLogout:', error);
            return false;
          }
        },
      };
    },
    {
      name: 'cart-storage',
      partialize: (state) => ({
        cartItems: state.cartItems,
        totalPrice: state.totalPrice,
        cartToken: state.cartToken,
        cartFetched: state.cartFetched,
      }),
      storage: {
        getItem: (name) => {
          const storage = getLocalStorage();
          if (!storage) return null;

          try {
            const value = storage.getItem(name);
            return value ? JSON.parse(value) : null;
          } catch (error) {
            console.error(`Error getting item ${name} from storage:`, error);
            return null;
          }
        },
        setItem: (name, value) => {
          const storage = getLocalStorage();
          if (!storage) return;

          try {
            storage.setItem(name, JSON.stringify(value));
          } catch (error) {
            console.error(`Error setting item ${name} in storage:`, error);
          }
        },
        removeItem: (name) => {
          const storage = getLocalStorage();
          if (!storage) return;

          try {
            storage.removeItem(name);
          } catch (error) {
            console.error(`Error removing item ${name} from storage:`, error);
          }
        },
      },
    }
  )
);

// NEW: Set up BroadcastChannel message listener
if (typeof window !== 'undefined' && cartChannel) {
  cartChannel.onmessage = (event) => {
    const { type, payload } = event.data;
    if (payload.tabId === TAB_ID) {
      return;
    }

    handlingBroadcast = true;
    try {
      const cartStore = useCartStore.getState();

      if (
        (type === 'CART_UPDATED' && cartStore.fetchInProgress) ||
        (type === 'ITEM_ADDED' && cartStore.addInProgress) ||
        (type === 'ITEM_REMOVED' && cartStore.removeInProgress) ||
        (type === 'CART_CLEARED' && cartStore.clearInProgress)
      ) {
        return;
      }

      switch (type) {
        case 'CART_UPDATED':
          if (payload.timestamp > lastCartFetchTime) {
            setTimeout(async () => {
              const result = await fetchCartFromServer(cartStore.cartToken, true);

              if (result.success) {
                silentlyUpdateState({
                  cartItems: result.items,
                  totalPrice: calculateTotalPrice(result.items),
                  cartFetched: true,
                });

                if (
                  result.cartToken &&
                  result.cartToken !== cartStore.cartToken
                ) {
                  setItemInStorage('cart_token', result.cartToken);
                  silentlyUpdateState({ cartToken: result.cartToken });
                }
                lastCartFetchTime = Date.now();
              }
            }, 150);
          }
          break;

        case 'ITEM_ADDED':
          if (payload.item) {
            const exists = cartStore.cartItems.some(
              (item) => item.assetId === payload.item.assetId
            );
            if (!exists) {
              const updatedCart = [...cartStore.cartItems, payload.item];
              silentlyUpdateState({
                cartItems: updatedCart,
                totalPrice: calculateTotalPrice(updatedCart),
              });
            }
          }
          break;

        case 'ITEM_REMOVED':
          if (payload.assetId) {
            const exists = cartStore.cartItems.some(
              (item) => item.assetId === payload.assetId
            );
            if (exists) {
              const updatedCart = cartStore.cartItems.filter(
                (item) => item.assetId !== payload.assetId
              );
              silentlyUpdateState({
                cartItems: updatedCart,
                totalPrice: calculateTotalPrice(updatedCart),
              });
            }
          }
          break;

        case 'CART_CLEARED':
          silentlyUpdateState({
            cartItems: [],
            totalPrice: 0,
          });
          break;

        case 'TOKEN_CHANGED':
          if (payload.token && payload.token !== cartStore.cartToken) {
            setItemInStorage('cart_token', payload.token);
            silentlyUpdateState({ cartToken: payload.token });
          }
          break;
      }
    } finally {
      setTimeout(() => {
        handlingBroadcast = false;
      }, 50);
    }
  };
}

// Handle tab visibility changes with BroadcastChannel
if (typeof window !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      const now = Date.now();
      if (now - lastCartFetchTime > MIN_CART_FETCH_INTERVAL) {
        setTimeout(() => {
          const cartStore = useCartStore.getState();
          if (cartStore && !cartStore.fetchInProgress) {
            cartStore.fetchCartContents(true);
          }
        }, 1000);
      }
    }
  });

  // NEW: Clean up BroadcastChannel on page unload
  window.addEventListener('beforeunload', () => {
    if (cartChannel) {
      cartChannel.close();
    }
  });
}

export default useCartStore;